import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/serviceConfigApi';

interface ServiceConfigState {
    configs: api.ServiceConfig[];
    selectedConfig: api.ServiceConfig | null;
    loading: boolean;
    error: string | null;
}

const initialState: ServiceConfigState = {
    configs: [],
    selectedConfig: null,
    loading: false,
    error: null,
};

export const fetchServiceConfigs = createAsyncThunk('serviceConfigs/fetchAll', async () => {
    const r = await api.getAllServiceConfigs();
    return r.data;
});

export const fetchServiceConfigById = createAsyncThunk('serviceConfigs/fetchById', async (id: string) => {
    const r = await api.getServiceConfigById(id);
    return r.data;
});

export const createServiceConfig = createAsyncThunk('serviceConfigs/create', async (data: api.ServiceConfigCreate) => {
    const r = await api.createServiceConfig(data);
    return r.data;
});

export const updateServiceConfig = createAsyncThunk('serviceConfigs/update', async ({ id, data }: { id: string; data: Partial<api.ServiceConfigCreate> }) => {
    const r = await api.updateServiceConfig(id, data);
    return r.data;
});

export const deleteServiceConfig = createAsyncThunk('serviceConfigs/delete', async (id: string) => {
    await api.deleteServiceConfig(id);
    return id;
});

const serviceConfigSlice = createSlice({
    name: 'serviceConfigs',
    initialState,
    reducers: {
        clearSelectedConfig: (state) => { state.selectedConfig = null; },
    },
    extraReducers: (builder) => {
        const loading = (state: ServiceConfigState) => { state.loading = true; state.error = null; };
        const failed  = (state: ServiceConfigState, action: any) => { state.loading = false; state.error = action.error.message ?? 'Error'; };

        builder
            .addCase(fetchServiceConfigs.pending, loading)
            .addCase(fetchServiceConfigs.fulfilled, (state, action) => { state.loading = false; state.configs = action.payload; })
            .addCase(fetchServiceConfigs.rejected, failed)

            .addCase(fetchServiceConfigById.pending, loading)
            .addCase(fetchServiceConfigById.fulfilled, (state, action) => { state.loading = false; state.selectedConfig = action.payload; })
            .addCase(fetchServiceConfigById.rejected, failed)

            .addCase(createServiceConfig.fulfilled, (state, action) => { state.configs.push(action.payload); })
            .addCase(updateServiceConfig.fulfilled, (state, action) => {
                const idx = state.configs.findIndex(c => c._id === action.payload._id);
                if (idx >= 0) state.configs[idx] = action.payload;
                state.selectedConfig = action.payload;
            })
            .addCase(deleteServiceConfig.fulfilled, (state, action) => {
                state.configs = state.configs.filter(c => c._id !== action.payload);
            });
    },
});

export const { clearSelectedConfig } = serviceConfigSlice.actions;
export default serviceConfigSlice.reducer;
